// Tank build (sump, rock, sand -> working water volume) in the keeper's
// unit. Storage is gallons; a litres keeper saw "104.4g", "Sump (gallons)"
// and "-5g vs your 19.812903926861132g label" (newkeeper audit 2026-10-01),
// and "g" reads as grams to many keepers. Same fractions as the server's
// tank-volume.js.

import { formatVolume, galToL, lToGal } from './units.js';

const ROCK = { none: 0, light: 0.1, medium: 0.18, heavy: 0.27 };
const SAND = { bare: 0, shallow: 0.05, deep: 0.12 };
const SUMP_FILL = 0.6;

const vol = (gal, unit) => formatVolume(gal, unit === 'L' ? 'L' : 'gal');

// Stored gallons -> the text an input shows in `unit`.
export function volumeInputText(gal, unit) {
  if (gal == null || gal === '' || !Number.isFinite(Number(gal))) return '';
  if (unit !== 'L') return String(Number(gal));
  return String(Math.round(galToL(Number(gal)) * 10) / 10);
}

// What the keeper typed in `unit` -> gallons. Blank is null; 0 stays 0
// ("no sump").
export function volumeInputToGallons(text, unit) {
  if (text == null) return null;
  const s = String(text).trim().replace(',', '.');
  if (s === '') return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return null;
  return unit === 'L' ? lToGal(n) : n;
}

export function buildDeltaLabel(workingGal, labelGal, unit) {
  const w = Number(workingGal);
  const l = Number(labelGal);
  if (!Number.isFinite(w) || !Number.isFinite(l)) return '';
  const d = w - l;
  const sign = d > 0 ? '+' : d < 0 ? '−' : '';
  return `${sign}${vol(Math.abs(d), unit)} vs your ${vol(l, unit)} label`;
}

export function buildPreview({ displayGal, sumpGal, rock, sand, unit }) {
  const display = Number(displayGal) || 0;
  if (display <= 0) return { working: 0, breakdown: 'Set a display volume in Tank details first.' };
  const r = ROCK[rock] ?? ROCK.medium;
  const s = SAND[sand] ?? SAND.shallow;
  const sumpRaw = Math.max(0, Number(sumpGal) || 0);
  const sump = sumpRaw * SUMP_FILL;
  const working = Math.round((display * (1 - r - s) + sump) * 10) / 10;
  const parts = [`${vol(display, unit)} display`];
  if (r > 0) parts.push(`− ${Math.round(r * 100)}% rock`);
  if (s > 0) parts.push(`− ${Math.round(s * 100)}% sand`);
  if (sump > 0) parts.push(`+ ${vol(sump, unit)} sump (60% of ${vol(sumpRaw, unit)})`);
  return { working, breakdown: parts.join(' '), delta: working - display };
}

// The build copy in the keeper's units: a litres keeper read "A 150 gallon
// tank..." and a sand bed in inches. The SPS hint on heavy rock is a reef
// word, left off a freshwater tank.
export function buildCopy(unit, { freshwater = false } = {}) {
  const litres = unit === 'L';
  return {
    lead: litres
      ? 'A 600 litre tank is rarely 600 litres of water once you add rock, sand, and a sump.'
      : 'A 150 gallon tank is rarely 150 gallons of water once you add rock, sand, and a sump.',
    sandShallow: litres ? '2–5 cm' : '1–2 in',
    sandDeep: litres ? '10 cm+' : '4 in+',
    rockHeavy: freshwater ? '−27%' : '−27% (SPS)',
  };
}
